import type { AthleteProfile } from "../../athlete-profile/types/athleteProfile";
import type {
  HistoricalFitImportPerformanceDiagnostics,
  HistoricalFitImportWorkerErrorPayload,
  HistoricalFitImportWorkerProgress,
  HistoricalFitImportWorkerRequestMessage,
  HistoricalFitImportWorkerResponseMessage,
  HistoricalFitImportWorkerStage,
  HistoricalFitImportWorkerState,
  HistoricalFitImportWorkerSuccessPayload
} from "./historicalFitWorkerProtocol";
import {
  createImportCorrelationId,
  createWorkerHandoffProfile,
  epochNowMs,
  finalizeWorkerHandoffProfile,
  type WorkerHandoffMarks,
  type WorkerHandoffProfile
} from "./workerHandoffProfiling";

export class HistoricalFitImportWorkerCancelledError extends Error {
  constructor() {
    super("Historical import cancelled.");
    this.name = "HistoricalFitImportWorkerCancelledError";
  }
}

export class HistoricalFitImportWorkerClientError extends Error {
  constructor(
    message: string,
    public readonly stage: HistoricalFitImportWorkerStage = "queued",
    public readonly details?: HistoricalFitImportWorkerErrorPayload
  ) {
    super(message);
    this.name = "HistoricalFitImportWorkerClientError";
  }
}

export type HistoricalFitWorkerDiagnostics = {
  stage: HistoricalFitImportWorkerStage;
  workerState: HistoricalFitImportWorkerState;
  lastProgressAt: string | null;
  performanceDiagnostics?: HistoricalFitImportPerformanceDiagnostics | null;
  handoffProfile?: WorkerHandoffProfile | null;
};

export type HistoricalFitImportWorkerClientStartOptions = {
  file: File;
  athleteProfile: AthleteProfile;
  selectionReceivedAtEpochMs?: number;
  onProgress?: (progress: HistoricalFitImportWorkerProgress, diagnostics: HistoricalFitWorkerDiagnostics) => void;
  onDiagnostics?: (diagnostics: HistoricalFitWorkerDiagnostics) => void;
};

export type HistoricalFitImportWorkerClient = {
  prepare?: () => Promise<void>;
  startImport: (options: HistoricalFitImportWorkerClientStartOptions) => Promise<HistoricalFitImportWorkerSuccessPayload>;
  cancel: () => void;
  getDiagnostics: () => HistoricalFitWorkerDiagnostics;
  getHandoffProfile?: () => WorkerHandoffProfile | null;
  applyHandoffMarks?: (patch: Partial<WorkerHandoffMarks>) => WorkerHandoffProfile | null;
};

type WorkerFactory = () => Worker;

type WorkerRuntimeState = {
  worker: Worker;
  instanceId: string;
  terminated: boolean;
  constructionRequestedAtEpochMs: number;
  constructorReturnedAtEpochMs: number;
  readyRequestedAtEpochMs: number | null;
  readyReceivedAtEpochMs: number | null;
  workerInstanceId: string | null;
  constructionStartedAt: number;
  readyStartedAt: number | null;
  ready: boolean;
  readyPromise: Promise<void> | null;
  resolveReady: (() => void) | null;
  rejectReady: ((error?: unknown) => void) | null;
  workerConstructionMs: number | null;
  workerReadyLatencyMs: number | null;
  currentResponseHandler: ((event: MessageEvent<HistoricalFitImportWorkerResponseMessage>) => void) | null;
  currentErrorHandler: ((event: Event) => void) | null;
  currentMessageErrorHandler: ((event: MessageEvent<unknown>) => void) | null;
};

function readFileBuffer(file: File): ArrayBuffer | Promise<ArrayBuffer> {
  if (typeof FileReaderSync !== "undefined") {
    const reader = new FileReaderSync();
    return reader.readAsArrayBuffer(file);
  }

  return file.arrayBuffer();
}

type ActiveRequest = {
  runtime: WorkerRuntimeState;
  requestId: string;
  correlationId: string;
  resolve: (value: HistoricalFitImportWorkerSuccessPayload) => void;
  reject: (reason?: unknown) => void;
  settled: boolean;
};

function createRequestId(): string {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function createDefaultWorkerFactory(): WorkerFactory {
  return () => new Worker(new URL("./historicalFitImport.worker.ts", import.meta.url), { type: "module" });
}

export function createHistoricalFitImportWorkerClient(
  workerFactory: WorkerFactory = createDefaultWorkerFactory()
): HistoricalFitImportWorkerClient {
  let activeRequest: ActiveRequest | null = null;
  let reusableRuntime: WorkerRuntimeState | null = null;
  let handoffProfile: WorkerHandoffProfile | null = null;
  let runtimeSequence = 0;
  let diagnostics: HistoricalFitWorkerDiagnostics = {
    stage: "queued",
    workerState: "idle",
    lastProgressAt: null,
    handoffProfile: null
  };

  const updateDiagnostics = (
    patch: Partial<HistoricalFitWorkerDiagnostics>,
    callback?: (diagnostics: HistoricalFitWorkerDiagnostics) => void
  ): HistoricalFitWorkerDiagnostics => {
    diagnostics = {
      ...diagnostics,
      ...patch
    };
    callback?.(diagnostics);
    return diagnostics;
  };

  const markProfile = (patch: Partial<WorkerHandoffMarks>): WorkerHandoffProfile | null => {
    if (!handoffProfile) {
      return null;
    }
    handoffProfile = finalizeWorkerHandoffProfile({
      ...handoffProfile,
      marks: { ...handoffProfile.marks, ...patch }
    });
    diagnostics = { ...diagnostics, handoffProfile };
    return handoffProfile;
  };

  const patchProfile = (patch: Partial<WorkerHandoffProfile>): WorkerHandoffProfile | null => {
    if (!handoffProfile) {
      return null;
    }
    handoffProfile = finalizeWorkerHandoffProfile({ ...handoffProfile, ...patch });
    diagnostics = { ...diagnostics, handoffProfile };
    return handoffProfile;
  };

  const disposeRuntime = (runtime: WorkerRuntimeState): void => {
    if (runtime.terminated) {
      return;
    }
    runtime.terminated = true;
    runtime.resolveReady = null;
    runtime.rejectReady = null;
    runtime.readyPromise = null;
    runtime.currentResponseHandler = null;
    runtime.currentErrorHandler = null;
    runtime.currentMessageErrorHandler = null;
    runtime.worker.onmessage = null;
    runtime.worker.onerror = null;
    runtime.worker.onmessageerror = null;
    runtime.worker.terminate();
    if (reusableRuntime === runtime) {
      reusableRuntime = null;
    }
  };

  const createRuntime = (): WorkerRuntimeState => {
    const constructionRequestedAtEpochMs = epochNowMs();
    const constructionStartedAt = performance.now();
    let worker: Worker;
    try {
      worker = workerFactory();
    } catch {
      throw new HistoricalFitImportWorkerClientError(
        "Historical import worker could not start in this browser. Import aborted to prevent UI freeze.",
        "queued"
      );
    }

    runtimeSequence += 1;
    const runtime: WorkerRuntimeState = {
      worker,
      instanceId: `runtime-${runtimeSequence}`,
      terminated: false,
      constructionRequestedAtEpochMs,
      constructorReturnedAtEpochMs: epochNowMs(),
      readyRequestedAtEpochMs: null,
      readyReceivedAtEpochMs: null,
      workerInstanceId: null,
      constructionStartedAt,
      readyStartedAt: null,
      ready: false,
      readyPromise: null,
      resolveReady: null,
      rejectReady: null,
      workerConstructionMs: null,
      workerReadyLatencyMs: null,
      currentResponseHandler: null,
      currentErrorHandler: null,
      currentMessageErrorHandler: null
    };

    // Listeners are attached synchronously in the construction task, so no worker
    // message (including READY) can be dispatched before they exist.
    runtime.worker.onmessage = (event: MessageEvent<HistoricalFitImportWorkerResponseMessage>) => {
      if (runtime.terminated) {
        return;
      }
      const message = event.data;
      if (!message) {
        return;
      }

      if (message.type === "READY") {
        runtime.readyReceivedAtEpochMs = epochNowMs();
        runtime.workerInstanceId = message.payload?.workerInstanceId ?? runtime.workerInstanceId;
        runtime.ready = true;
        runtime.workerConstructionMs = Math.max(0, performance.now() - runtime.constructionStartedAt);
        runtime.workerReadyLatencyMs = runtime.readyStartedAt === null ? 0 : Math.max(0, performance.now() - runtime.readyStartedAt);
        runtime.resolveReady?.();
        runtime.resolveReady = null;
        runtime.rejectReady = null;
        return;
      }

      runtime.currentResponseHandler?.(event);
    };

    runtime.worker.onerror = (event: Event) => {
      if (runtime.terminated) {
        return;
      }
      runtime.rejectReady?.(event);
      runtime.currentErrorHandler?.(event);
    };

    runtime.worker.onmessageerror = (event: MessageEvent<unknown>) => {
      if (runtime.terminated) {
        return;
      }
      runtime.rejectReady?.(event);
      runtime.currentMessageErrorHandler?.(event);
    };

    return runtime;
  };

  const getOrCreateRuntime = (): WorkerRuntimeState => {
    if (reusableRuntime && !reusableRuntime.terminated) {
      return reusableRuntime;
    }

    const runtime = createRuntime();
    reusableRuntime = runtime;
    return runtime;
  };

  const ensureWorkerReady = (runtime: WorkerRuntimeState): Promise<void> => {
    if (runtime.ready) {
      // READY already delivered; never wait on an event that has already fired.
      return Promise.resolve();
    }

    if (runtime.readyPromise) {
      return runtime.readyPromise;
    }

    runtime.readyStartedAt = performance.now();
    runtime.readyRequestedAtEpochMs = epochNowMs();
    runtime.readyPromise = new Promise<void>((resolve, reject) => {
      if (runtime.ready) {
        resolve();
        return;
      }
      runtime.resolveReady = () => {
        runtime.ready = true;
        runtime.workerConstructionMs = Math.max(0, performance.now() - runtime.constructionStartedAt);
        runtime.workerReadyLatencyMs = runtime.readyStartedAt === null ? 0 : Math.max(0, performance.now() - runtime.readyStartedAt);
        runtime.readyStartedAt = null;
        resolve();
      };
      runtime.rejectReady = reject;
    });

    return runtime.readyPromise;
  };

  const finalizeActiveRequest = (reason: "success" | "cancelled" | "failed", error?: unknown): void => {
    const current = activeRequest;
    if (!current || current.settled) {
      return;
    }

    current.settled = true;
    current.runtime.currentResponseHandler = null;
    current.runtime.currentErrorHandler = null;
    current.runtime.currentMessageErrorHandler = null;

    if (reason !== "success") {
      disposeRuntime(current.runtime);
    } else {
      current.runtime.terminated = false;
      reusableRuntime = current.runtime;
    }

    if (reason === "success") {
      return;
    }

    if (reason === "cancelled") {
      current.reject(new HistoricalFitImportWorkerCancelledError());
      return;
    }

    current.reject(error);
  };

  return {
    prepare: async () => {
      const runtime = getOrCreateRuntime();
      await ensureWorkerReady(runtime);
    },

    startImport: async options => {
      if (activeRequest && !activeRequest.settled) {
        throw new HistoricalFitImportWorkerClientError("Historical worker is already processing another file.", diagnostics.stage);
      }

      const correlationId = createImportCorrelationId();
      handoffProfile = createWorkerHandoffProfile({
        correlationId,
        fileName: options.file.name,
        fileBytes: options.file.size
      });
      markProfile({ selectionReceivedAtEpochMs: options.selectionReceivedAtEpochMs ?? epochNowMs() });

      const runtime = getOrCreateRuntime();
      markProfile({
        workerConstructionRequestedAtEpochMs: runtime.constructionRequestedAtEpochMs,
        workerConstructorReturnedAtEpochMs: runtime.constructorReturnedAtEpochMs
      });

      const requestId = createRequestId();
      const workerReadyPromise = ensureWorkerReady(runtime);
      markProfile({ workerReadyRequestedAtEpochMs: runtime.readyRequestedAtEpochMs ?? runtime.constructorReturnedAtEpochMs });
      updateDiagnostics({
        stage: "queued",
        workerState: "starting",
        lastProgressAt: new Date().toISOString(),
        handoffProfile
      }, options.onDiagnostics);

      const mainThreadFileReadStartedAt = performance.now();
      markProfile({ fileReadStartedAtEpochMs: epochNowMs() });
      const fileBufferResult = readFileBuffer(options.file);
      let fileReadMs = 0;
      let mainThreadToWorkerMs = 0;

      return await new Promise<HistoricalFitImportWorkerSuccessPayload>((resolve, reject) => {
        activeRequest = {
          runtime,
          requestId,
          correlationId,
          resolve,
          reject,
          settled: false
        };

        runtime.currentResponseHandler = (event: MessageEvent<HistoricalFitImportWorkerResponseMessage>) => {
          const message = event.data;
          if (message.type === "READY") {
            return;
          }

          if (!activeRequest || activeRequest.settled) {
            return;
          }

          // Stale-message guard: request id must match, and when the worker supplies a
          // correlation id it must match the correlation id of the active import.
          if (message.requestId !== activeRequest.requestId) {
            return;
          }
          if (typeof message.correlationId === "string" && message.correlationId !== activeRequest.correlationId) {
            return;
          }

          if (message.type === "PROGRESS") {
            const nextDiagnostics = updateDiagnostics({
              stage: message.payload.stage,
              workerState: "running",
              lastProgressAt: message.payload.emittedAt
            }, options.onDiagnostics);
            options.onProgress?.(message.payload, nextDiagnostics);
            return;
          }

          if (message.type === "SUCCESS") {
            const timings = message.payload.workerHandoffTimings;
            markProfile({
              mainThreadResultReceivedAtEpochMs: epochNowMs(),
              workerMessageReceivedAtEpochMs: timings?.receivedAtEpochMs ?? handoffProfile?.marks.workerMessageReceivedAtEpochMs ?? null,
              workerProcessingStartedAtEpochMs: timings?.processingStartedAtEpochMs ?? null,
              workerProcessingCompletedAtEpochMs: timings?.processingCompletedAtEpochMs ?? null,
              workerResultPostedAtEpochMs: timings?.resultPostedAtEpochMs ?? null
            });
            updateDiagnostics({
              stage: "complete",
              workerState: "completed",
              lastProgressAt: new Date().toISOString(),
              performanceDiagnostics: message.payload.performanceDiagnostics ?? null,
              handoffProfile
            }, options.onDiagnostics);
            const current = activeRequest;
            finalizeActiveRequest("success");
            if (current && !current.settled) {
              current.resolve(message.payload);
            } else {
              resolve(message.payload);
            }
            activeRequest = null;
            return;
          }

          if (message.type === "CANCELLED") {
            updateDiagnostics({
              workerState: "cancelled",
              lastProgressAt: new Date().toISOString()
            }, options.onDiagnostics);
            finalizeActiveRequest("cancelled");
            activeRequest = null;
            return;
          }

          if (message.type === "ERROR") {
            updateDiagnostics({
              stage: message.payload.stage,
              workerState: "failed",
              lastProgressAt: new Date().toISOString()
            }, options.onDiagnostics);
            finalizeActiveRequest("failed", new HistoricalFitImportWorkerClientError(message.payload.message, message.payload.stage, message.payload));
            activeRequest = null;
          }
        };

        runtime.currentErrorHandler = () => {
          updateDiagnostics({
            workerState: "failed",
            lastProgressAt: new Date().toISOString()
          }, options.onDiagnostics);
          finalizeActiveRequest("failed", new HistoricalFitImportWorkerClientError("Historical FIT worker crashed unexpectedly.", diagnostics.stage));
          activeRequest = null;
        };

        runtime.currentMessageErrorHandler = () => {
          updateDiagnostics({
            workerState: "failed",
            lastProgressAt: new Date().toISOString()
          }, options.onDiagnostics);
          finalizeActiveRequest("failed", new HistoricalFitImportWorkerClientError("Historical FIT worker emitted an unreadable message.", diagnostics.stage));
          activeRequest = null;
        };

        const postImportToWorker = (buffer: ArrayBuffer, handoffStrategy: "transferable_arraybuffer" | "cloned_arraybuffer" = "transferable_arraybuffer") => {
          fileReadMs = Math.max(0, performance.now() - mainThreadFileReadStartedAt);
          const mainThreadToWorkerPostStartedAt = performance.now();
          const postedAtEpochMs = epochNowMs();
          markProfile({ payloadPostMessageStartedAtEpochMs: postedAtEpochMs });
          const message: HistoricalFitImportWorkerRequestMessage = {
            type: "START_IMPORT",
            requestId,
            correlationId,
            postedAtEpochMs,
            payload: {
              fileBuffer: buffer,
              fileName: options.file.name,
              fileSizeBytes: options.file.size,
              fileType: options.file.type,
              lastModified: options.file.lastModified,
              athleteProfile: options.athleteProfile,
              sourceType: options.file.name.toLowerCase().endsWith(".fit.gz") ? "fit.gz" : "fit",
              compressionState: options.file.name.toLowerCase().endsWith(".fit.gz") ? "fit.gz" : "fit",
              inputBinaryType: "arraybuffer",
              handoffStrategy,
              workerConstructionMs: runtime.workerConstructionMs,
              workerReadyLatencyMs: runtime.workerReadyLatencyMs,
              mainThreadReadMs: fileReadMs,
              mainThreadToWorkerMs: 0,
              mainThreadFileReadMs: fileReadMs,
              mainThreadToWorkerPostMs: 0
            }
          };
          const transferList = buffer.byteLength > 0 ? [buffer] : [];
          try {
            runtime.worker.postMessage(message, transferList);
            mainThreadToWorkerMs = Math.max(0, performance.now() - mainThreadToWorkerPostStartedAt);
            message.payload.mainThreadToWorkerMs = mainThreadToWorkerMs;
            message.payload.mainThreadToWorkerPostMs = mainThreadToWorkerMs;
          } catch {
            const fallbackMessage: HistoricalFitImportWorkerRequestMessage = {
              ...message,
              payload: {
                ...message.payload,
                handoffStrategy: "cloned_arraybuffer",
                inputBinaryType: "arraybuffer-cloned"
              }
            };
            runtime.worker.postMessage(fallbackMessage);
            mainThreadToWorkerMs = Math.max(0, performance.now() - mainThreadToWorkerPostStartedAt);
            fallbackMessage.payload.mainThreadToWorkerMs = mainThreadToWorkerMs;
            fallbackMessage.payload.mainThreadToWorkerPostMs = mainThreadToWorkerMs;
          }
          markProfile({ payloadPostMessageReturnedAtEpochMs: epochNowMs() });
        };

        void (async () => {
          try {
            const [buffer] = await Promise.all([fileBufferResult, workerReadyPromise]);
            fileReadMs = Math.max(0, performance.now() - mainThreadFileReadStartedAt);
            markProfile({
              fileReadCompletedAtEpochMs: epochNowMs(),
              workerReadyReceivedAtEpochMs: runtime.readyReceivedAtEpochMs ?? epochNowMs()
            });
            patchProfile({ workerInstanceId: runtime.workerInstanceId });

            postImportToWorker(buffer, "transferable_arraybuffer");
          } catch (error) {
            if (!activeRequest || activeRequest.settled) {
              return;
            }
            updateDiagnostics({
              workerState: "failed",
              lastProgressAt: new Date().toISOString()
            }, options.onDiagnostics);
            finalizeActiveRequest("failed", new HistoricalFitImportWorkerClientError("Historical FIT worker could not receive the file payload.", diagnostics.stage));
            activeRequest = null;
            reject(error);
          }
        })();
      });
    },

    cancel: () => {
      if (!activeRequest || activeRequest.settled) {
        return;
      }
      updateDiagnostics({
        workerState: "cancelled",
        lastProgressAt: new Date().toISOString()
      });
      finalizeActiveRequest("cancelled");
      activeRequest = null;
    },

    getDiagnostics: () => diagnostics,

    getHandoffProfile: () => handoffProfile,

    applyHandoffMarks: patch => markProfile(patch)
  };
}
